import React, { type FC, useState, useCallback, useEffect, useRef } from 'react';
import { Coordination } from '@atlassiansox/engagekit-ts';

import { N500, N20, B50 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import { SpotlightTarget, Spotlight, SpotlightTransition } from '@atlaskit/onboarding';
import { LinkItem } from '@atlaskit/side-navigation';
import BoardIcon from '@atlaskit/icon/glyph/board';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { AdControlProvider } from '@atlassian/ad-control-toolkit';

import { useRouteName } from '@confluence/route-manager';
import { Attribution, TransparentErrorBoundary, ErrorDisplay } from '@confluence/error-boundary';
import { SPACE_JIRA_PROJECT_BOARD } from '@confluence/named-routes';
import { useCoordinationClient } from '@confluence/engagement-provider';
import {
	confluenceLocalStorageInstance,
	PERSISTED_KEYS_ON_SERVER,
} from '@confluence/storage-manager';

import { useUserState } from '../hooks/useUserState';

import { AdMenu } from './AdMenu';

export const SPOTLIGHT_MESSAGE_ID = 'jira-board-in-confl-spotlight-message-id';
export const SPOTLIGHT_TARGET_ID = 'jira-board-in-confluence-spotlight-id';

const DISMISSALS_STORAGE_KEY = PERSISTED_KEYS_ON_SERVER.PERSISTED_JIRA_AD_DISMISSAL;

type JiraBoardNavigationItemProps = {
	spaceKey: string;
	isNavItemVisible: boolean;
};

// Copied from confluence/next/packages/side-navigation/src/SpaceNavigation/SpaceLinks.tsx, needed for consistency with the other menu items
const cssFn = ({ isSelected = false, isDatabaseIcon = false }) => ({
	padding: `0px ${token('space.050', '4px')} 0px ${token('space.100', '8px')}`,
	height: '36px',
	'min-height': '36px',
	'border-radius': '3px',
	'font-size': '14px',
	span: {
		columnGap: token('space.100', '8px'),
	},
	backgroundColor: isSelected ? token('color.background.selected', B50) : 'transparent',
	'&:hover': {
		backgroundColor: isSelected
			? token('color.background.selected.hovered', '#cce0ff')
			: token('color.background.neutral.subtle.hovered', N20),
	},
	'&, &:visited, &:active, &:focus': {
		color: isSelected ? token('color.text.selected', '#0c66e4') : token('color.text.subtle', N500),
	},
	textDecoration: 'none !important',
	'[data-item-elem-before=true]': {
		'margin-right': '0px',
		height: '24px',
		width: '24px',
		'img, span, svg': {
			height: isDatabaseIcon ? '18px' : '20px',
			width: isDatabaseIcon ? '18px' : '20px',
		},
	},
});

export const JiraBoardNavigationItemBase: FC<JiraBoardNavigationItemProps> = ({
	spaceKey,
	isNavItemVisible,
}) => {
	const [isSpotlightActive, setIsSpotlightActive] = useState(true);
	const coordinationClient = useCoordinationClient();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { hasJira, isSiteAdmin, isLoading, userStateDataError } = useUserState();
	const hasSpotlightScreenViewedEventBeenFired = useRef(false);

	const isOnJiraBoardRouteArgs = {
		selector: (routeName: string | undefined) => routeName === SPACE_JIRA_PROJECT_BOARD.name,
	};
	const isOnJiraBoardRoute = useRouteName(isOnJiraBoardRouteArgs);

	const [isAdDismissed, setIsAdDismissed] = useState<boolean>(
		confluenceLocalStorageInstance.getItemAsBoolean(DISMISSALS_STORAGE_KEY),
	);

	// The ad control dismissal is relevant only until they provision Jira
	const showAdMenu = !hasJira && !isLoading && !userStateDataError;

	const FireSpotlightScreenViewedEvent: FC = () => {
		const { createAnalyticsEvent } = useAnalyticsEvents();

		useEffect(() => {
			if (!hasSpotlightScreenViewedEventBeenFired.current) {
				createAnalyticsEvent({
					type: 'sendScreenEvent',
					data: {
						name: 'jiraBoardSpotlight',
						source: 'JiraBoardNavigationItemNudge',
					},
				}).fire();
				hasSpotlightScreenViewedEventBeenFired.current = true;
			}
		}, [createAnalyticsEvent]);

		return null;
	};

	const onDismiss = useCallback(async () => {
		setIsSpotlightActive(false);
		await coordinationClient.stop(SPOTLIGHT_MESSAGE_ID);
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'jiraBoardSpotlightDismissed',
				source: 'JiraBoardNavigationItemNudge',
			},
		}).fire();
	}, [coordinationClient, createAnalyticsEvent]);

	const onTryNow = useCallback(async () => {
		setIsSpotlightActive(false);
		await coordinationClient.stop(SPOTLIGHT_MESSAGE_ID);
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'jiraBoardSpotlightTryNow',
				source: 'JiraBoardNavigationItemNudge',
			},
		}).fire();
	}, [coordinationClient, createAnalyticsEvent]);

	const dismissAd = useCallback(async () => {
		confluenceLocalStorageInstance.setItem(DISMISSALS_STORAGE_KEY, true);
		setIsAdDismissed(true);
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'notInterested',
				source: 'JiraBoardNavigationAdControl',
			},
		}).fire();
	}, [createAnalyticsEvent]);

	// The ad control dismissal is relevant only until they provision Jira
	if (!hasJira && isAdDismissed) {
		return null;
	}

	return (
		<>
			{userStateDataError && <ErrorDisplay error={userStateDataError} />}
			<SpotlightTarget name={SPOTLIGHT_TARGET_ID}>
				<LinkItem
					href={SPACE_JIRA_PROJECT_BOARD.toUrl({ spaceKey })}
					iconBefore={<BoardIcon size="small" label="" />}
					iconAfter={showAdMenu && <AdMenu onDismiss={dismissAd} isSiteAdmin={isSiteAdmin} />}
					// eslint-disable-next-line @atlaskit/design-system/no-deprecated-apis
					cssFn={cssFn}
					isSelected={isOnJiraBoardRoute}
				>
					Jira project
				</LinkItem>
			</SpotlightTarget>
			<SpotlightTransition>
				{isSpotlightActive && isNavItemVisible && (
					<Coordination client={coordinationClient} messageId={SPOTLIGHT_MESSAGE_ID}>
						<>
							<FireSpotlightScreenViewedEvent />
							<Spotlight
								actions={[
									{
										href: SPACE_JIRA_PROJECT_BOARD.toUrl({ spaceKey }),
										onClick: async () => {
											await onTryNow();
										},
										text: 'Try now',
									},
									{
										onClick: onDismiss,
										text: 'Dismiss',
									},
								]}
								heading="Manage projects within Confluence"
								target={SPOTLIGHT_TARGET_ID}
								key={SPOTLIGHT_TARGET_ID}
								targetRadius={3}
								dialogPlacement="right bottom"
								dialogWidth={275}
							>
								Link real-time data to any document, get automatic updates, and easily create
								reports.
							</Spotlight>
						</>
					</Coordination>
				)}
			</SpotlightTransition>
		</>
	);
};

export const JiraBoardNavigationItem: FC<JiraBoardNavigationItemProps> = (props) => {
	return (
		<TransparentErrorBoundary attribution={Attribution.GROWTH_TRAILBLAZERS}>
			<AdControlProvider>
				<JiraBoardNavigationItemBase {...props} />
			</AdControlProvider>
		</TransparentErrorBoundary>
	);
};
